import { Block } from '@klp/design';
import { theme } from '@klp/design/Theme/theme';

import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    ListItemIcon,
    makeStyles,
    MenuItem,
    OutlinedInput,
    Select,
} from '@material-ui/core';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { frontpageActions, frontpageSelectors } from '../store/frontpageSlice';
import { FrontpageCardTypes } from '../utils';

const useStyles = makeStyles(() => ({
    selector: {
        width: '100%',
        minWidth: theme.spacing(25),
    },
    chip: {
        padding: 1,
        border: '2px solid red',
        fontSize: '16px',
        borderColor: theme.palette.secondary.main,
        backgroundColor: 'white',
    },
    box: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    text: {
        paddingLeft: 8,
    },
    selectContainer: {
        '& .MuiSelect-selectMenu': {
            overflowY: 'scroll',
            maxHeight: '45px',
        },
    },
    sticky: {
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        width: '100%',
        zIndex: 1,
    },
}));

export default function FrontpageCardsSelector() {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const { storedSelectedFrontpageCards } = useSelector(frontpageSelectors);

    const { saveSelectedFrontpageCardsToStorage } = frontpageActions;

    const [selectedFrontpageCards, setSelectedFrontpageCards] = useState<
        Array<string>
    >(storedSelectedFrontpageCards || []);

    const frontpageCardTypes = Object.values(FrontpageCardTypes);

    const [open, setOpen] = useState(false);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleSaveAndClose = () => {
        setOpen(false);
        dispatch(saveSelectedFrontpageCardsToStorage(selectedFrontpageCards));
    };

    const handleFrontpageCardsSelect = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const value = event.target.value as Array<string>;
        const validValues = value.filter((v) => v !== null && v !== undefined);
        setSelectedFrontpageCards(validValues);
    };

    useEffect(() => {
        if (
            storedSelectedFrontpageCards &&
            storedSelectedFrontpageCards?.length > 0
        ) {
            setSelectedFrontpageCards(storedSelectedFrontpageCards);
        }
    }, [storedSelectedFrontpageCards]);

    return (
        <Block paddingBottom="tiny">
            <FormControl className={classes.selector}>
                <InputLabel
                    className={classes.text}
                    id="demo-multiple-checkbox-label"
                >
                    {t('frontPage.adjustFrontpage.select')}
                </InputLabel>
                <Select
                    className={classes.selectContainer}
                    labelId="demo-multiple-checkbox-label"
                    id="demo-multiple-checkbox"
                    multiple={true}
                    value={selectedFrontpageCards}
                    open={open}
                    onChange={handleFrontpageCardsSelect}
                    onClose={handleSaveAndClose}
                    onOpen={handleOpen}
                    input={<OutlinedInput label="Tag" />}
                    renderValue={(selected: Array<string>) => (
                        <Box className={classes.box}>
                            {selected.map((value: string) => (
                                <Chip
                                    className={classes.chip}
                                    key={value}
                                    label={t(
                                        `frontPage.adjustFrontpage.frontpageCardsSelector.${value}`,
                                    )}
                                />
                            ))}
                        </Box>
                    )}
                >
                    {frontpageCardTypes?.map((name) => (
                        <MenuItem key={name} value={name}>
                            <ListItemIcon>
                                <Checkbox
                                    checked={
                                        selectedFrontpageCards.indexOf(name) >
                                        -1
                                    }
                                />
                            </ListItemIcon>
                            {t(
                                `frontPage.adjustFrontpage.frontpageCardsSelector.${name}`,
                            )}
                        </MenuItem>
                    ))}
                    <Block
                        paddingLeft="compact"
                        paddingTop="compact"
                        paddingBottom="compact"
                        className={classes.sticky}
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleSaveAndClose}
                        >
                            {t('users.save')}
                        </Button>
                    </Block>
                </Select>
            </FormControl>
        </Block>
    );
}
